











































import { mapActions } from 'vuex'
import mixins from 'vue-typed-mixins'
import { ADD_TOAST_MESSAGE } from 'vuex-toast'
import AuthLayout from '@/layout/AuthLayout.vue'
import Language from '@/components/Language/Language.vue'
import PasswordForm from '@/abstract/PasswordForm'
import { AUTH__CHECK, AUTH__REGISTER_CONFIRM } from '@/store/actions'
import store from '@/store'
import { RouteNames } from '@/router'
import { isDevelopHost } from '@/constants/enviroment'

import type { AxiosPromise } from 'axios'
import type { Route, RawLocation } from 'vue-router'
import type { RegisterPayload } from '@/store/modules/auth'

interface Data {
  step: 1 | 2
  fname: string
  lname: string
  company: string
  confirmation: string
  isBlockAutofill: boolean
  isPasswordDirty: boolean
  isConfirmDirty: boolean
  isDevelopHost: boolean
}

interface Methods {
  [AUTH__REGISTER_CONFIRM]: (data: RegisterPayload) => AxiosPromise
  registerUser: () => void
  handleNextStep: () => void
  handlePrevStep: () => void
  handlePasswordBlur: () => void
  handleConfirmBlur: () => void
  autofillHack: () => void
}

interface Computed {
  key: string
  title: string
  text: string
  isPasswordConfirmed: boolean
  isPasswordHasLength: boolean
  passwordError: string
  canGo: boolean
}

async function verifyCode(
  to: Route,
  from: Route,
  next: (to?: string) => void,
) {
  if (to.params?.key) {
    try {
      const isCodeValid = await store.dispatch(AUTH__CHECK, to.params.key)

      if (!isCodeValid) {
        return next(RouteNames.InvalidLink)
      }
    } catch (e) {
      return next(RouteNames.InvalidLink)
    }
  }

  next()
}

export default mixins(PasswordForm).extend<Data, Methods, Computed, {}>({
  beforeRouteEnter: verifyCode,
  beforeRouteUpdate: verifyCode,
  components: {
    AuthLayout,
    Language,
  },
  data: () => ({
    step: 1,
    fname: '',
    lname: '',
    confirmation: '',
    company: '',
    isBlockAutofill: false,
    isPasswordDirty: false,
    isConfirmDirty: false,
    isDevelopHost,
  }),
  computed: {
    key(): string {
      return this.$route.params.key
    },
    title(): string {
      switch (this.step) {
        case 1:
          return this.$tc('auth.emailConfirmed')
        case 2:
          return this.$tc('auth.niceToMeetYou', 1, {
            fname: this.fname,
            lname: this.lname,
          })
        default:
          return ''
      }
    },
    text(): string {
      switch (this.step) {
        case 1:
          return this.$tc('auth.meetText')
        case 2:
          return this.$tc('auth.passwordAdvice')
        default:
          return ''
      }
    },
    isPasswordConfirmed(): boolean {
      return this.password === this.confirmation
    },
    isPasswordHasLength(): boolean {
      return !!this.password && this.password.length > 5
    },
    passwordError(): string {
      if (!this.isPasswordConfirmed && this.isConfirmDirty) {
        return this.$tc('auth.passwordMatchError')
      }
      if (!this.isPasswordHasLength && this.isPasswordDirty) {
        return this.$tc('auth.passwordLengthError')
      }
      return ''
    },
    canGo(): boolean {
      if (this.step === 1 && !!this.fname.trim().length && !!this.lname.trim().length) {
        return true
      }
      if (
        this.step === 2 &&
        this.isPasswordConfirmed &&
        this.isPasswordHasLength &&
        this.isPasswordHasCapital &&
        this.isPasswordHasRegular &&
        this.isPasswordHasNumbers &&
        !this.isPasswordHasCyrillic
      ) {
        return true
      }
      return false
    },
  },
  watch: {
    step(val, oldVal) {
      if (val !== oldVal) {
        this.autofillHack()
      }
    },
  },
  methods: {
    ...mapActions([AUTH__REGISTER_CONFIRM]),
    registerUser(): void {
      this[AUTH__REGISTER_CONFIRM]({
        key: this.key,
        fname: this.fname.trim(),
        lname: this.lname.trim(),
        password: this.password,
      }).then(response => {
        this.$store.dispatch(ADD_TOAST_MESSAGE, {
          text: this.$tc('auth.passwordComplete'),
          type: 'success',
        })
        const location: RawLocation =
          response.data.companyId !== null
            ? '/scripts'
            : { name: RouteNames.CompanyCreate }
        this.$router.push(location)
      })
    },
    handleNextStep(): void {
      if (!this.canGo) {
        return
      }
      if (this.step === 2) {
        return this.registerUser()
      }
      this.step++
    },
    handlePrevStep(): void {
      if (this.step <= 1) {
        return
      }
      this.step--
    },
    handlePasswordBlur(): void {
      this.isPasswordDirty = true
    },
    handleConfirmBlur(): void {
      this.isConfirmDirty = true
    },
    autofillHack(): void {
      this.isBlockAutofill = true
      setTimeout(() => {
        this.isBlockAutofill = false
      }, 100)
    },
  },
  metaInfo() {
    return { title: this.$tc('auth.compliteSinUp') }
  },
})
